import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Landmark, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Wind, Sofa } from 'lucide-react';

const GovernmentBuildingCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "What government facilities do you clean?",
      a: "Council and local government buildings, including kitchens, canopies, sports centres, lunch areas, toilets and common work areas. Scope is built around what each facility needs."
    },
    {
      q: "Is this scheduled cleaning, or do you handle one-off requests too?",
      a: "Mostly scheduled, around 90% of our government work runs on a regular schedule, with the remainder handled as ad-hoc call-outs as needed."
    },
    {
      q: "Do you clean kitchens and canopies in government facilities?",
      a: "Yes, kitchen and canopy cleaning, along with equipment cleaning, is part of our government building work, using the same standards we apply to commercial kitchen exhaust systems."
    },
    {
      q: "Do you work with government facilities beyond Perth?",
      a: "Our current government work is based in the Perth metro area, and we're building toward larger government contracts across WA as we complete ISO certification."
    }
  ];

  // Service Schema for SEO - Local Government Areas
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Government Building Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Armadale", "Bayswater", "Belmont", "Canning", "Cockburn", "Fremantle", 
      "Gosnells", "Joondalup", "Kalamunda", "Kwinana", "Mandurah", "Melville", 
      "Nedlands", "Perth", "Rockingham", "South Perth", "Stirling", "Subiaco", 
      "Swan", "Wanneroo"
    ],
    "description": "Reliable, scheduled cleaning for government and council facilities across Perth. Kitchen and canopy cleaning, sports centres, common areas, windows and carpets."
  };

  const relatedServices = [
    {
      title: 'Canopy, Duct & Rooftop Fan Cleaning',
      description: 'AS 1851:2012 compliant exhaust cleaning for government kitchen facilities.',
      link: '/services/canopy-duct-fan-cleaning-perth',
      icon: Wind
    },
    {
      title: 'Commercial Carpet Cleaning',
      description: 'Carpet cleaning for offices and common areas.',
      link: '/services/commercial-carpet-cleaning-perth',
      icon: Sofa
    }
  ];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/industries/government-building-cleaning-perth" />
        <title>Government Building Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Reliable cleaning for government and council facilities across Perth. Kitchens, canopies, sports centres, common areas. Scheduled maintenance with documentation." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="government-building-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.pexels.com/photos/6761495/pexels-photo-6761495.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Perth city skyline with modern buildings"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Landmark size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Government & Council</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Government Building Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Reliable, scheduled cleaning for government and council facilities across Perth
              </p>
              <p className="text-gray-400 mb-8">
                Government and council facilities need a cleaning contractor that shows up consistently and delivers the documentation and reliability these contracts require. Extreme Clean Australia provides cleaning for local government sites across Perth, covering kitchens, canopies, sports centres, lunch areas and more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Government building cleaning covers kitchen and canopy cleaning, equipment cleaning, rotary tile and grout cleaning and extraction, toilets and common areas in work spaces, lunch and common area cleaning, sports centre cleaning, and window and carpet cleaning. Scope is built around each facility, whether it's a single council building or a larger site with a sports centre and kitchen facilities.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Kitchen and canopy cleaning',
                  'Equipment cleaning',
                  'Rotary tile and grout cleaning',
                  'Toilets and common areas',
                  'Lunch area cleaning',
                  'Sports centre cleaning',
                  'Window cleaning',
                  'Carpet cleaning'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mostly Scheduled */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Reliability</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Mostly Scheduled, With Flexibility for Ad-Hoc Work</h2>
              <p className="text-gray-600">
                Around 90% of our government and council work runs on a regular scheduled basis, with the remaining 10% handled as ad-hoc call-outs when something comes up outside the normal schedule. That reliability, showing up when we say we will, is exactly what government and council contracts require.
              </p>
            </div>
          </div>
        </section>

        {/* Building Toward Larger Contracts */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Growth</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Building Toward Larger Government and Mining Contracts</h2>
              <p className="text-gray-300">
                We're actively working toward ISO certification to take on larger government and mining contracts, this is a genuine priority for the business, not a claim we're making before we're ready. In the meantime, we already provide cleaning for local government facilities, and we're building the certifications and processes needed to take that further.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Local Government Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Local Government Areas We Service</h2>
              <p className="text-gray-600">
                We provide government building cleaning across local government areas throughout the Perth metro region, including Armadale, Bayswater, Belmont, Canning, Cockburn, Fremantle, Gosnells, Joondalup, Kalamunda, Kwinana, Mandurah, Melville, Nedlands, Perth, Rockingham, South Perth, Stirling, Subiaco, Swan and Wanneroo.
              </p>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Government Facility Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for government and council building cleaning across Perth.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GovernmentBuildingCleaning;
